import { FC, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { PersonAdd as PersonAddIcon, PersonRemove as PersonRemoveIcon } from "@mui/icons-material";
import { useRoleUsersHook } from "../../../hooks/useRbacHook";
import { TUserForRole } from "../../../apis/rbac/rbac.interfaces";

interface RoleUsersViewProps {
  roleId: string;
}

const RoleUsersView: FC<RoleUsersViewProps> = ({ roleId }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    users,
    total,
    isLoading,
    error,
    assignUser,
    unassignUser,
    isAssigning,
    isUnassigning,
  } = useRoleUsersHook({
    roleId,
    page: page + 1,
    limit: pageSize,
  });

  const handleAssign = async (user: TUserForRole) => {
    try {
      await assignUser(user.id);
    } catch {
      // Error handled by mutation
    }
  };

  const handleUnassign = async (user: TUserForRole) => {
    try {
      await unassignUser(user.id);
    } catch {
      // Error handled by mutation
    }
  };

  if (error) {
    return (
      <Typography color="error" data-testid="role-users-error">
        Failed to load users.
      </Typography>
    );
  }

  return (
    <Box data-testid="role-users-view">
      <Typography variant="h6" gutterBottom data-testid="role-users-title">
        Users
      </Typography>
      <Card data-testid="role-users-card">
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress data-testid="role-users-loading" />
            </Box>
          ) : users.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="120px"
              gap={1}
              data-testid="role-users-empty"
            >
              <Typography variant="body2" color="text.secondary">
                No users found for this institute
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} data-testid={`role-user-row-${user.id}`}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.isAssignedToRole ? "Assigned" : "Unassigned"}
                            size="small"
                            color={user.isAssignedToRole ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {user.isAssignedToRole ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<PersonRemoveIcon />}
                              onClick={() => handleUnassign(user)}
                              disabled={isUnassigning}
                              data-testid={`unassign-user-${user.id}`}
                            >
                              Remove
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleAssign(user)}
                              disabled={isAssigning}
                              data-testid={`assign-user-${user.id}`}
                            >
                              Assign
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                data-testid="role-users-pagination"
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RoleUsersView;
